import React from "react";
import { ArrowRightOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../components/application/components/LanguageSelector";

function Index() {
  const { t } = useTranslation();

  const features = [
    {
      icon: (
        <svg className="w-16 h-16 text-[#09c35c]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: t("main.features.time.title"),
      description: t("main.features.time.description")
    },
    {
      icon: (
        <svg className="w-16 h-16 text-[#09c35c]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2H5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 10h10M7 14h4" />
        </svg>
      ),
      title: t("main.features.access.title"),
      description: t("main.features.access.description")
    },
    {
      icon: (
        <svg className="w-16 h-16 text-[#09c35c]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      title: t("main.features.usability.title"),
      description: t("main.features.usability.description")
    }
  ];

  const steps = [
    t("main.steps.0"),
    t("main.steps.1"),
    t("main.steps.2"),
    t("main.steps.3"),
    t("main.steps.4"),
    t("main.steps.5")
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <header className="bg-white shadow-md fixed w-full z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <a href="/">
            <img src="/assets/images/onedose_black.png" alt="Logo" className="h-12" />
          </a>
          <div className="flex items-center space-x-4">
            <a href="/login" className="text-[#09c35c] text-lg hover:text-green-600 transition-colors">
              {t("main.buttons.loginNow")}
            </a>
            <a href="/register-phone" className="bg-[#09c35c] text-white px-6 py-2 rounded-md hover:bg-green-600 transition-colors flex items-center">
              {t("main.buttons.registerNow")}
            </a>
            <LanguageSelector />
          </div>
        </div>
      </header>

      <main className="flex-grow pt-20 bg-gray-100">
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 flex flex-col-reverse lg:flex-row items-center">
            <div className="lg:w-1/2">
              <h1 className="grid gap-3 text-4xl md:text-5xl font-extrabold mb-4">
                <p>{t("main.hero.headline1")}</p>
                <span className="text-[#09c35c]">{t("main.hero.headline2")}</span>
              </h1>
              <p className="text-lg md:text-xl mb-6">{t("main.hero.description1")}</p>
              <p className="text-lg md:text-xl mb-4">
                {t("main.hero.description2")} {" "}
                <a href="https://www.hastane.başkent.edu.tr/1142.html" target="_blank" rel="noopener noreferrer" className="text-[#09c35c] font-bold underline hover:text-green-600 transition-colors">
                  {t("main.hero.clickHere")}
                </a>
              </p>
              <div className="grid items-center space-y-4">
                <a href="/register-phone" className="inline-flex items-center bg-[#09c35c] text-white px-6 py-3 rounded-md hover:bg-green-600 transition-colors">
                  {t("main.buttons.registerNow")} <ArrowRightOutlined className="w-5 h-5 ml-2" />
                </a>
                <a href="/login" className="inline-flex items-center bg-gray-100 text-[#09c35c] px-6 py-3 rounded-md hover:bg-gray-200 transition-colors">
                  {t("main.buttons.loginNow")}
                </a>
              </div>
            </div>
            <div className="lg:w-1/2 mb-8 lg:mb-0">
              <img src="./assets/images/MicrosoftTeams-image.png" alt="Illustration" className="w-full max-w-md mx-auto rounded-lg shadow-lg" />
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#09c35c] mb-6">
              {t("main.sectionTitles.beSure")}
            </h2>
            <p className="text-lg md:text-xl">{t("main.hero.longText")}</p>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center text-center">
                  {feature.icon}
                  <h3 className="text-2xl font-bold mt-4 text-[#09c35c]">{feature.title}</h3>
                  <p className="mt-2 text-lg">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <img src="./assets/images/Group37.png" alt="Second Opinion" className="w-full rounded-lg" />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#09c35c] mb-6">
                {t("main.sectionTitles.whenToApply")}
              </h2>
              <ul className="space-y-4">
                {Array.from({ length: 6 }).map((_, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="w-6 h-6 text-[#09c35c] flex-shrink-0 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-lg">{t(`main.whenToApply.${index}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#09c35c] mb-6">
                {t("main.sectionTitles.howItWorks")}
              </h2>
              <ol className="space-y-4">
                {steps.map((step, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-[#09c35c] font-bold text-xl mr-1">{index + 1}.</span>
                    <span className="text-lg">{step}</span>
                  </li>
                ))}
              </ol>
              <div className="grid items-center space-y-4 mt-12">
                <a href="/register-phone" className="inline-flex items-center bg-[#09c35c] text-white px-6 py-3 rounded-md hover:bg-green-600 transition-colors">
                  {t("main.buttons.registerNow")} <ArrowRightOutlined className="w-5 h-5 ml-2" />
                </a>
                <a href="/login" className="inline-flex items-center bg-gray-100 text-[#09c35c] px-6 py-3 rounded-md hover:bg-gray-200 transition-colors">
                  {t("main.buttons.loginNow")}
                </a>
              </div>
            </div>
            <div>
              <img src="./assets/images/close-up-doctor-filling-out-medical-form-photo-with-copy-space.png" alt="How it Works" className="w-full rounded-lg shadow-lg" />
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-gray-100 py-6">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-md">{t("main.footer.copyright")}</p>
        </div>
      </footer>
    </div>
  );
}

export default Index;